import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { getIncidentById } from "./api/incidents.api";
import IncidentDetails from "./IncidentDetails";
import PinButton from "./components/pins/PinButton";
import Button from "./ui/Button";
import DrawerA11yWrapper from "./ui/DrawerA11yWrapper";
import { colors, radius, shadows, spacing, typography } from "./ui/tokens";

export default function IncidentDetailDrawer({
  incident,
  incidentId,
  onClose,
  onIncidentUpdated,
  canEdit = false,
  canManage = false,
  onOpenFullPage,
}) {
  const titleId = useId();
  const closeRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [loadedIncident, setLoadedIncident] = useState(null);

  const resolvedIncident = incident || loadedIncident || null;
  const resolvedIncidentId = incident?.id || incidentId || null;

  const loadIncident = useCallback(async () => {
    if (incident || !incidentId) {
      setLoadedIncident(null);
      return;
    }
    try {
      const response = await getIncidentById(incidentId);
      const payload = response?.data ?? response;
      setLoadedIncident(payload || null);
    } catch (_err) {
      setLoadedIncident(null);
    }
  }, [incident, incidentId]);

  useEffect(() => {
    setIsFullScreen(false);
  }, [resolvedIncidentId]);

  useEffect(() => {
    loadIncident();
  }, [loadIncident]);

  if (!resolvedIncidentId) return null;

  return (
    <DrawerA11yWrapper
      open={Boolean(resolvedIncidentId)}
      onClose={onClose}
      labelledBy={titleId}
      initialFocusRef={closeRef}
      backdropStyle={isFullScreen ? expandedBackdropStyle : overlayStyle}
      containerStyle={isFullScreen ? expandedDrawerStyle : drawerStyle}
    >
      <div style={contentStyle}>
        <div style={headerStyle}>
          <div style={{ display: "grid", gap: 2 }}>
            <div id={titleId} style={{ fontSize: typography.size.lg, fontWeight: typography.weight.bold, color: colors.text }}>
              {resolvedIncident?.title || "Incident Details"}
            </div>
            <div style={headerSubtitleStyle}>
              {isFullScreen ? "Full window" : "Side panel"}
            </div>
          </div>
          <div style={headerActionsStyle}>
            {resolvedIncident ? (
              <PinButton
                itemType="incident"
                itemId={resolvedIncident.id}
                summary={{
                  title: resolvedIncident.title || `Incident #${resolvedIncident.id}`,
                  moduleLabel: "Incident",
                  secondaryText: [resolvedIncident.category, resolvedIncident.severity].filter(Boolean).join(" | ") || null,
                  status: resolvedIncident.status || null,
                  ownerDisplay: resolvedIncident.owner_id || null,
                  updatedAt: resolvedIncident.updated_at || resolvedIncident.reported_at || null,
                }}
              />
            ) : null}
            {typeof onOpenFullPage === "function" ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenFullPage(resolvedIncident?.id || resolvedIncidentId)}
                aria-label="Open incident details page"
              >
                Open Page
              </Button>
            ) : null}
            {isFullScreen ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsFullScreen(false)}
                aria-label="Minimize incident details to side panel"
              >
                Minimize to Side Panel
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsFullScreen(true)}
                aria-label="Open incident details in full window"
              >
                Open in Full Window
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              style={closeButtonStyle}
              aria-label="Close incident drawer"
              ref={closeRef}
            >
              X
            </Button>
          </div>
        </div>
        <div style={bodyStyle}>
          <IncidentDetails
            incident={resolvedIncident}
            onUpdated={onIncidentUpdated}
            canEdit={canEdit}
            canManage={canManage}
            shellStyle={{ padding: 0, border: "none", borderRadius: 0 }}
          />
        </div>
      </div>
    </DrawerA11yWrapper>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.4)",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "stretch",
  zIndex: 3000,
};

const drawerStyle = {
  width: "720px",
  maxWidth: "92vw",
  height: "100%",
  background: colors.surface,
  display: "flex",
  flexDirection: "column",
  boxShadow: shadows.md,
};

const expandedBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "16px",
  zIndex: 3000,
};

const expandedDrawerStyle = {
  width: "calc(100vw - 48px)",
  maxWidth: "1240px",
  height: "calc(100vh - 32px)",
  maxHeight: "calc(100vh - 32px)",
  background: colors.surface,
  display: "flex",
  flexDirection: "column",
  boxShadow: shadows.md,
  borderRadius: radius.xl,
};

const contentStyle = {
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flex: 1,
  minHeight: 0,
};

const headerStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.md,
  padding: "16px 20px",
  borderBottom: `1px solid ${colors.border}`,
  background: colors.surface,
};

const headerSubtitleStyle = {
  fontSize: typography.size.sm,
  color: colors.textMuted,
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
};

const closeButtonStyle = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: typography.size.base,
  color: colors.textMuted,
  padding: "4px 8px",
};

const bodyStyle = {
  padding: "16px 20px 20px",
  overflowY: "auto",
  flex: 1,
};
